import {
  Accordion,
  AccordionTrigger,
  AccordionContent,
  AccordionItem,
} from "@/components/ui/accordion";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AddDeleteCartButtons } from "modules/cart/components/cartElements";
import { AppDataContext } from "modules/main/context/appData";
import { useCart } from "modules/cart/hooks/useCart";
import { useFetch } from "modules/main/hooks/useFetch";
import {
  Check,
  Circle,
  ShoppingCart,
  SquarePlus,
  TrendingDown,
} from "lucide-react";
import React, {
  forwardRef,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { endPointProduct } from "modules/main/services/endpoints";
import { FetchDataOptions } from "modules/main/services/fetchData";
import {
  PriceStr,
  PromoStr,
  StoreDetails,
} from "modules/product/types/product";
import Amount from "utils/amount";
import { get_total_qty_per_prod } from "utils/cartUtils";
import { getStoreBdData } from "utils/storeDataManager";
import { PriceChart } from "modules/product/components/price-chart";
import { AuthError } from "modules/authentication/types/form-login-types";
import { NoAuthError } from "modules/authentication/components/no-auth-error";
import { GeneralError } from "modules/main/components/general-error";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { StoreData } from "modules/main/types/storeData";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import posthog from "posthog-js";
import { getVisitorId } from "utils/analytics";
import { useRequireLocationForCart } from "modules/cart/hooks/useRequireLocationForCart";
import { LocationRequiredDialog } from "modules/main/components/location-required-dialog";

const sendEventPH = () => {
  posthog.capture("Añadir al carrito");
};

const getProductCategoryId = (product: ProductData): number => {
  return product.category_id;
};

interface ProductData {
  id: string;
  brand: string;
  category_id: number;
  slug: string;
  total?: number;
  base_unit?: string;
  unit?: string;
  price_detail: PriceStr;
  stores: StoreDetails[];
}

interface ProductDetailDataResponse {
  product_data: ProductData;
  similars_products: ProductData[];
  chart_data: any[];
}

interface ProductSheetProps extends React.HTMLAttributes<HTMLDivElement> {
  productID: string;
  isMobile?: boolean;
}

const calculateAveragePrice3Months = (chartData: any[]) => {
  if (!chartData || chartData.length === 0) return 0;

  // Tomar los últimos 3 meses
  const lastThreeMonths = chartData.slice(-3);

  // Extraer todos los precios de esos meses
  const allPrices: number[] = [];

  lastThreeMonths.forEach((monthData) => {
    Object.keys(monthData).forEach((key) => {
      // Excluir las claves que no son precios
      if (key !== "month" && key !== "display_month") {
        allPrices.push(monthData[key]);
      }
    });
  });

  // Calcular promedio
  return allPrices.length > 0
    ? allPrices.reduce((sum, price) => sum + price, 0) / allPrices.length
    : 0;
};

export const ProductSheet = forwardRef<HTMLDivElement, ProductSheetProps>(
  ({ productID, ...props }, ref) => {
    const lowStockTreeshold = 50;
    const { storeList } = useContext(AppDataContext);

    const requestOptions: FetchDataOptions = {
      method: "GET",
      headers: {},
      body: undefined,
    };
    const { addDeletefromCart, cart, validating } = useCart();
    const {
      isLocationDialogOpen,
      executeWithLocationRequirement,
      handleLocationSelected,
      handleLocationDialogOpenChange,
    } = useRequireLocationForCart();
    const { data, error, loading } = useFetch<ProductDetailDataResponse>(
      endPointProduct(productID),
      requestOptions,
    );

    const { product_data, chart_data } = data ?? {
      product_data: null,
      chart_data: [],
    };

    const inCart = get_total_qty_per_prod(product_data?.id || "", cart);
    const storesLength = product_data?.stores.length ?? 0;
    const bestIndex =
      product_data?.stores.findIndex(
        (store) => store.sku === product_data?.price_detail.best_price_id,
      ) ?? 0;
    const validBestIndex = bestIndex <= storesLength - 1 ? bestIndex : 0;
    const [selectedIndex, setIndex] = useState(validBestIndex);

    useLayoutEffect(() => {
      if (selectedIndex !== validBestIndex) {
        setIndex(validBestIndex);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validBestIndex]);

    const selectedStore =
      product_data?.stores[selectedIndex] ??
      product_data?.stores[validBestIndex];

    const storeData = getStoreBdData(
      Number(selectedStore?.store_id),
      storeList,
    );

    const averagePrice =
      (product_data?.stores.reduce((acc, store) => acc + store.price, 0) ?? 0) /
      (storesLength || 1);

    const averagePrice3Months = calculateAveragePrice3Months(chart_data);

    const handleAdd = (
      mainId: string,
      product: StoreDetails,
      category_id: number,
    ) => {
      window.dataLayer.push({
        event: "add_to_cart",
        product_id: product.s_id,
        visitor_id: getVisitorId(),
      });
      addDeletefromCart({
        id: mainId,
        category_id,
        sku: product.sku,
        name: product.name,
        quantity: inCart + 1,
        method: "SAME", //TODO - Change to a dynamic method
      });
      validating({
        id: mainId,
        sku: product.sku,
        name: product.name,
        category_id,
        quantity: 1,
        method: "SAME", // FIXME
      });
    };

    const handleAddWithLocationValidation = (
      mainId: string,
      product: StoreDetails,
      category_id: number,
    ) => {
      executeWithLocationRequirement(() => {
        sendEventPH();
        handleAdd(mainId, product, category_id);
      });
    };
    if (error) {
      const ErrorComponent =
        error instanceof AuthError ? NoAuthError : GeneralError;
      return <ErrorComponent />;
    }

    if (loading) {
      return <ProductSheetSkeleton isMobile={props?.isMobile} />;
    }

    if (product_data === null || selectedStore === undefined)
      return <ProductSheetSkeleton isMobile={props?.isMobile} />;

    window.dataLayer.push({
      event: "view_product_detail",
      product_id: product_data.stores[selectedIndex]?.s_id,
      visitor_id: getVisitorId(),
    });
    const imageUrl = selectedStore.images?.detail ?? selectedStore.image_url;
    return (
      <div className="m-auto lg:py-8 py-4 px-4 lg:w-3/4">
        <div className="flex flex-col items-start w-full">
          <h5 className="font-semibold text-2xl break-words">{selectedStore?.name}</h5>
          <div className="flex flex-row justify-between w-full gap-4 flex-wrap p-4">
            <div className="flex flex-col gap-3 w-full lg:w-5/12">
              <div className="flex  flex-col">
                <img
                  className="Image-170 mx-auto lg:mx-0"
                  src={imageUrl}
                  alt={selectedStore?.name}
                />
              </div>
              <div className="flex flex-col text-sm gap-1">
                <p className="text-muted-foreground font-semibold">
                  Marca:{" "}
                  <span className="text-sm font-semibold text-foreground leading-none">
                    {product_data?.brand}
                  </span>{" "}
                </p>
                <p className="text-muted-foreground font-semibold">
                  Cantidad:{" "}
                  <span className="text-sm font-semibold text-foreground leading-none">
                    {product_data?.total} {product_data?.base_unit}
                  </span>{" "}
                </p>
                <p className="text-muted-foreground font-semibold">
                  Precio Promedio:{" "}
                  <span className="text-sm font-semibold text-foreground leading-none">
                    <Amount
                      amount={averagePrice}
                      currency="CLP"
                      locale="es-CL"
                    />
                  </span>{" "}
                </p>
                <p className="text-muted-foreground font-semibold">
                  Precio Promedio 3 meses:{" "}
                  <span className="text-sm font-semibold text-foreground leading-none">
                    <Amount
                      amount={averagePrice3Months}
                      currency="CLP"
                      locale="es-CL"
                    />
                  </span>
                </p>
                {
                  // (selectedStore?.available < lowStockTreeshold) &&
                  // <div>
                  //     <Badge variant="destructive">¡Bajo Stock!</Badge>
                  // </div>
                }
                <p className="text-muted-foreground">
                  {selectedStore?.description}
                </p>
                <div className="flex justify-center mt-8 w-full">
                  {inCart > 0 ? (
                    <AddDeleteCartButtons
                      className="gap-8 w-full justify-center"
                      product_id={product_data?.id ?? 0}
                      category_id={getProductCategoryId(product_data)}
                      sku={selectedStore?.sku ?? ""}
                      name={selectedStore?.name ?? ""}
                      quantity={inCart}
                      method="SAME"
                      showDelete={false}
                      PlusMinusCallback={addDeletefromCart}
                    />
                  ) : (
                    <Button
                      data-ph-event="add-to-cart"
                      onClick={() => {
                        handleAddWithLocationValidation(
                          product_data.id,
                          selectedStore,
                          getProductCategoryId(product_data),
                        );
                      }}
                    >
                      <SquarePlus className="mr-2 h-4 w-4" />
                      Añadir
                    </Button>
                  )}
                </div>
              </div>
              {(data?.similars_products?.length ?? 0) > 0 && (
                <>
                  <Separator className="my-4" />
                  {!props?.isMobile && (
                    <div className="text-sm font-semibold">
                      Productos similares:
                      <SimmilarProducts
                        product={data?.similars_products ?? []}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="lg:w-[50%]">
              <div className="flex flex-col gap-2">
                <ProductPrice
                  storeId={selectedStore.sku}
                  price={
                    selectedIndex === bestIndex
                      ? product_data.price_detail.best_price
                      : selectedStore.price
                  }
                  averageThreeMonths={averagePrice3Months}
                  noPromoPrice={selectedStore.price}
                  storeImgUrl={storeData.logo_url}
                  storeName={storeData.name}
                  isBestPrice={selectedIndex === bestIndex}
                  url={selectedStore?.url ?? ""}
                  promos={selectedStore?.promos ?? []}
                  isSelected={true}
                />
                {storesLength > 1 && (
                  <>
                    <p className="text-sm font-bold mt-2">Otras Altenativas</p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tienda</TableHead>
                          <TableHead>Precio</TableHead>
                          <TableHead>Promociones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {product_data?.stores
                          .filter((_st, index) => index !== selectedIndex)
                          .map((store, index) => {
                            const storeAlternativesData = getStoreBdData(
                              Number(store.store_id),
                              storeList,
                            );
                            return (
                              <TableRow key={store.sku}>
                                <TableCell>
                                  <div className="flex flex-row items-center gap-4">
                                    <a
                                      href={store.url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <Avatar className={"h-6 w-6"}>
                                        <AvatarImage
                                          src={storeAlternativesData.logo_url}
                                        />
                                      </Avatar>
                                    </a>
                                    <span className="text-xs text-muted-foreground">
                                      {storeAlternativesData.name}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell
                                  className={cn("font-semibold", {
                                    "worst-price":
                                      store.sku ===
                                      product_data?.price_detail.worst_price_id,
                                  })}
                                >
                                  <Amount
                                    amount={store.price}
                                    currency="CLP"
                                    locale="es-CL"
                                  />
                                </TableCell>
                                <TableCell>
                                  {store.promos.map((promo, index) => {
                                    const amount = (
                                      <Amount
                                        amount={promo.price}
                                        currency="CLP"
                                        locale="es-CL"
                                      />
                                    );
                                    return (
                                      <div
                                        key={`${store.sku}-${promo.type}-${promo.price}-${promo.qty ?? index}`}
                                        className="col-span-4"
                                      >
                                        <Badge variant={"secondary"}>
                                          {promo.type === "NxM" ? (
                                            <span>
                                              ¡Lleva {promo.qty} y paga {amount}
                                              !
                                            </span>
                                          ) : (
                                            <span>
                                              ¡Precio Oferta {amount}!
                                            </span>
                                          )}
                                        </Badge>
                                      </div>
                                    );
                                  })}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </>
                )}
                <Separator className="my-4" />
                <p className="text-sm font-bold">
                  Historico de precio por tienda
                </p>
                <PriceChart chartData={chart_data} storeList={storeList} />
                {props?.isMobile && (
                  <>
                    <Separator className="my-4" />
                    <div className="text-sm font-semibold">
                      Productos similares:
                      <SimmilarProducts
                        product={data?.similars_products ?? []}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <LocationRequiredDialog
            open={isLocationDialogOpen}
            onOpenChange={handleLocationDialogOpenChange}
            onLocationSelected={handleLocationSelected}
          />
        </div>
      </div>
    );
  },
);

interface ProductPriceProps extends React.HTMLAttributes<HTMLDivElement> {
  storeId: string;
  price: number;
  noPromoPrice: number;
  averageThreeMonths: number;
  storeImgUrl: string;
  storeName: string;
  isSelected: boolean;
  isBestPrice: boolean;
  url: string;
  promos: PromoStr[];
  handler?: () => void;
}

const ProductPrice = forwardRef<HTMLDivElement, ProductPriceProps>(
  ({ storeId, price, noPromoPrice, storeImgUrl, storeName, ...props }, ref) => {
    return (
      <div className="flex" ref={ref}>
        <div className="flex w-full grid-rows-1 gap-4">
          <div className="">
            <span
              className={`${props.isSelected ? "font-extrabold text-2xl" : "font-bold text-sm text-muted-foreground"} ${props.isBestPrice ? "best-price" : ""}`}
            >
              <Amount amount={price} currency="CLP" locale="es-CL" />
            </span>
            {price !== noPromoPrice && (
              <span
                className={`line-through mx-4 text-muted-foreground ${!props.isSelected && "text-xs"}`}
              >
                <Amount amount={noPromoPrice} currency="CLP" locale="es-CL" />
              </span>
            )}
          </div>
          <div className="flex flex-row items-center gap-4">
            <a href={props.url} target="_blank" rel="noreferrer">
              <Avatar className={"h-6 w-6"}>
                <AvatarImage src={storeImgUrl} />
              </Avatar>
            </a>
            <span className="text-xs text-muted-foreground">{storeName}</span>
          </div>
          <div className="flex w-full flex-wrap gap-2 h-fit">
            {price < props.averageThreeMonths && (
              <Badge className="ml-2">
                <TrendingDown className="mr-2 h-4 w-4" />
                <span>Bajo el promedio</span>
              </Badge>
            )}
            {props.promos.map((promo, index) => {
              const amount = (
                <Amount amount={promo.price} currency="CLP" locale="es-CL" />
              );
              return (
                <div
                  key={`${storeId}-${promo.type}-${promo.price}-${promo.qty ?? index}`}
                >
                  <Badge variant={props.isSelected ? "default" : "secondary"}>
                    {promo.type === "NxM" ? (
                      <span>
                        ¡Lleva {promo.qty} y paga {amount}!
                      </span>
                    ) : (
                      <span>¡Precio Oferta {amount}!</span>
                    )}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  },
);

interface SimmilarProductsProps extends React.HTMLAttributes<HTMLDivElement> {
  product: ProductData[];
}

const SimmilarProducts = forwardRef<HTMLDivElement, SimmilarProductsProps>(
  ({ product, ...props }, ref) => {
    const { storeList } = useContext(AppDataContext);
    return (
      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full max-w-[70dvw] lg:max-w-[90dvw] m-auto"
      >
        <CarouselContent>
          {product.map((product) => (
            <CarouselItem
              key={product.id}
              className="basis-1/1  xl:basis-1/3 lg:basis-1/2"
            >
              <div className="p-1">
                <SimmilarProductCard product={product} storeList={storeList} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious />
        <CarouselNext />
      </Carousel>
    );
  },
);

const SimmilarProductCard = forwardRef<
  HTMLDivElement,
  { product: ProductData; storeList: StoreData[] }
>(({ product, storeList }, ref) => {
  const selectedStore =
    product.stores[
      product.stores.findIndex(
        (store) => store.sku === product.price_detail.best_price_id,
      )
    ];
  const storeData = getStoreBdData(Number(selectedStore?.store_id), storeList);
  const imageUrl = selectedStore.images?.catalog ?? selectedStore.image_url;
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-4 text-xs min-h-32 max-w-52">
        <img className="Image-80" src={imageUrl} alt={selectedStore?.name} />
        <span className="font-semibold">
          <Link to={"/" + product.slug}>{selectedStore?.name}</Link>
        </span>
        <span className="text-muted-foreground">{product.brand}</span>
      </CardContent>
      <CardFooter>
        <div className="flex flex-row w-full gap-2 justify-center items-center">
          <a href={selectedStore.url} target="_blank" rel="noreferrer">
            <Avatar className={"h-6 w-6"}>
              <AvatarImage src={storeData.logo_url} />
            </Avatar>
          </a>
          <span className="font-bold  text-sm">
            <Amount
              amount={selectedStore?.price ?? 0}
              currency="CLP"
              locale="es-CL"
            />
          </span>
        </div>
      </CardFooter>
    </Card>
  );
});

interface ProductSheetSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  isMobile?: boolean;
}

const ProductSheetSkeleton = forwardRef<
  HTMLDivElement,
  ProductSheetSkeletonProps
>(({ isMobile, ...props }, ref) => {
  const alternativeStores = Array.from({ length: 3 });
  const similarProducts = Array.from({ length: 3 });

  return (
    <div className="m-auto lg:py-8 py-4 px-4 w-full lg:w-3/4" ref={ref}>
      <div className="flex flex-col items-start w-full">
        <Skeleton className="mb-4 h-8 w-2/3 lg:w-1/3" />
        <div className="flex flex-row justify-between w-full gap-4 flex-wrap p-4">
          <div className="flex flex-col gap-3 w-full lg:w-5/12">
            <div className="flex flex-col">
              <Skeleton className="Image-170 mx-auto h-[170px] w-full max-w-[170px] lg:mx-0" />
            </div>
            <div className="flex flex-col text-sm gap-2">
              <Skeleton className="w-1/2 h-6" />
              <Skeleton className="w-1/2 h-6" />
              <Skeleton className="w-1/2 h-6" />
              <Skeleton className="w-2/3 h-6" />
              <Skeleton className="w-full h-16" />
              <div className="mt-8 flex justify-center w-full">
                <Skeleton className="h-10 w-full max-w-sm" />
              </div>
            </div>
            {!isMobile && (
              <>
                <Separator className="my-4" />
                <div className="flex flex-col gap-3 w-full">
                  <Skeleton className="h-5 w-40" />
                  <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
                    {similarProducts.map((_, index) => (
                      <Skeleton
                        key={`desktop-similar-${index}`}
                        className="h-60 w-full rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="w-full lg:w-[50%]">
            <div className="flex flex-col gap-2 justify-start">
              <div className="flex flex-col gap-4 rounded-lg border p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-8 w-1/3" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <div className="flex items-center gap-4">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Skeleton className="h-6 w-28 rounded-full" />
                  <Skeleton className="h-6 w-32 rounded-full" />
                </div>
              </div>
              <Skeleton className="mt-2 h-5 w-40" />
              <div className="rounded-lg border">
                <div className="grid grid-cols-3 gap-4 border-b px-4 py-3">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
                {alternativeStores.map((_, index) => (
                  <div
                    key={`alternative-store-${index}`}
                    className="grid grid-cols-3 gap-4 border-b px-4 py-4 last:border-b-0"
                  >
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <Skeleton className="h-5 w-48" />
              <Skeleton className="w-full h-48 mb-4" />
              {isMobile && (
                <>
                  <Separator className="my-4" />
                  <div className="flex flex-col gap-3 w-full">
                    <Skeleton className="h-5 w-40" />
                    <div className="grid gap-3">
                      {similarProducts.map((_, index) => (
                        <Skeleton
                          key={`mobile-similar-${index}`}
                          className="h-40 w-full rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

